// Public authentication utilities for booking flow
// This is separate from the main auth store to avoid triggering global auth state changes

import { 
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  type User
} from 'firebase/auth';
import { firebaseApp } from '@/boot/firebase'

// Get auth instance (same as main app but used differently)
const publicFirebaseAuth = getAuth(firebaseApp);

// Configure Google Auth Provider for public booking
const publicGoogleProvider = new GoogleAuthProvider();
publicGoogleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Configure Apple Auth Provider for public booking
const publicAppleProvider = new OAuthProvider('apple.com');
publicAppleProvider.addScope('email');
publicAppleProvider.addScope('name');

// Listen for auth state changes and manage localStorage tokens
onAuthStateChanged(publicFirebaseAuth, async (user) => {
  if (user) {
    try {
      // User is signed in, get and store token
      const token = await user.getIdToken();
      localStorage.setItem('publicAuthToken', token);
      console.log('Public auth token stored for user:', user.email);
      
      // Import and call user profile API
      // We use dynamic import to avoid circular dependency
      const { usePublicStore } = await import('@/stores/publicStore');
      const publicStore = usePublicStore();
      
      // Try to get user profile - handle 404s gracefully
      try {
        await publicStore.getUserProfile();
        console.log('User profile found for authenticated user');
      } catch (profileError: any) {
        // Handle 404 as "profile not found" rather than an auth error
        if (profileError?.response?.status === 404) {
          console.log('User profile not found (404), user may need to create one during booking');
          // DO NOT remove auth token or log user out - just note that profile needs creation
        } else {
          console.warn('Error checking user profile:', profileError);
          // For other errors, we still keep the user logged in
        }
      }
    } catch (tokenError) {
      console.error('Failed to get token for public auth user:', tokenError);
      // Only remove token if we can't get the Firebase token itself
      localStorage.removeItem('publicAuthToken');
    }
  } else {
    // User is signed out, remove token and clear profile
    localStorage.removeItem('publicAuthToken');
    console.log('Public auth token removed - user signed out');
    
    // Clear user profile from store
    try {
      const { usePublicStore } = await import('@/stores/publicStore');
      const publicStore = usePublicStore();
      publicStore.clearUserProfile();
    } catch (error) {
      console.warn('Failed to clear user profile on sign out:', error);
    }
  }
});

// Public authentication helper functions (no global state changes)
export const publicAuth = {
  // Sign in with Google for public booking (no global state update)
  signInWithGoogle: async (): Promise<{ user: User | null; error: string | null }> => {
    try {
      const result = await signInWithPopup(publicFirebaseAuth, publicGoogleProvider);
      
      // Return user data without triggering global auth state changes
      return { user: result.user, error: null };
    } catch (error: any) {
      return { user: null, error: error.message };
    }
  },

  // Sign in with Apple for public booking (no global state update)
  signInWithApple: async (): Promise<{ user: User | null; error: string | null }> => {
    try {
      const result = await signInWithPopup(publicFirebaseAuth, publicAppleProvider);
      
      // Return user data without triggering global auth state changes
      return { user: result.user, error: null };
    } catch (error: any) {
      return { user: null, error: error.message };
    }
  },

  // Sign in with email and password for public booking
  signInWithEmailAndPassword: async (email: string, password: string): Promise<{ user: User | null; error: string | null }> => {
    try {
      const result = await signInWithEmailAndPassword(publicFirebaseAuth, email, password);
      
      // Return user data without triggering global auth state changes
      return { user: result.user, error: null };
    } catch (error: any) {
      let errorMessage = 'Failed to sign in';
      
      // Handle specific Firebase errors
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled';
      }
      
      return { user: null, error: errorMessage };
    }
  },

  // Create user with email and password for public booking
  createUserWithEmailAndPassword: async (email: string, password: string, displayName?: string): Promise<{ user: User | null; error: string | null }> => {
    try {
      const result = await createUserWithEmailAndPassword(publicFirebaseAuth, email, password);
      
      // Update display name if provided
      if (displayName && result.user) {
        await updateProfile(result.user, { displayName });
      }
      
      // Return user data without triggering global auth state changes
      return { user: result.user, error: null };
    } catch (error: any) {
      let errorMessage = 'Failed to create account';
      
      // Handle specific Firebase errors
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      }
      
      return { user: null, error: errorMessage };
    }
  },

  // Get current user without triggering global auth
  getCurrentUser: () => {
    return publicFirebaseAuth.currentUser;
  },

  // Get fresh token from Firebase
  getToken: async (): Promise<string | null> => {
    const currentUser = publicFirebaseAuth.currentUser;
    if (currentUser) {
      try {
        return await currentUser.getIdToken();
      } catch (error) {
        console.error('Failed to get Firebase token:', error);
        return null;
      }
    }
    return null;
  },

  // Refresh token and update localStorage
  refreshToken: async (): Promise<string | null> => {
    const currentUser = publicFirebaseAuth.currentUser;
    if (currentUser) {
      try {
        // Force refresh the token
        const token = await currentUser.getIdToken(true);
        localStorage.setItem('publicAuthToken', token);
        return token;
      } catch (error) {
        console.error('Failed to refresh Firebase token:', error);
        localStorage.removeItem('publicAuthToken');
        return null;
      }
    }
    localStorage.removeItem('publicAuthToken');
    return null;
  },

  // Sign out from public session only
  signOut: async () => {
    try {
      const currentUser = publicFirebaseAuth.currentUser;
      if (currentUser) {
        console.log('Signing out user from Firebase:', currentUser.email);
        // Actually sign out from Firebase
        await publicFirebaseAuth.signOut();
        localStorage.removeItem('publicAuthToken');
        console.log('Public auth session ended successfully');
      }
      return { error: null };
    } catch (error: any) {
      console.error('Failed to sign out from Firebase:', error);
      // Still clear localStorage even if Firebase signOut fails
      localStorage.removeItem('publicAuthToken');
      return { error: error.message };
    }
  }
};
